import { httpDelete, httpGet, httpPost } from "@/api/connection";
import { FilesAPI } from "@/Features/Files/files.api";
import type { UploaderApi } from "@/Features/Files/Uploader";

import type {
  CompleteEvidencePhotoUploadBody,
  CompleteEvidencePhotoUploadResult,
  DeleteEvidencePhotoData,
  GetEvidencePhotosQuery,
  GetEvidencePhotosResult,
  RenameEvidencePhotoData,
  StartEvidencePhotoUploadBody,
  StartEvidencePhotoUploadResult,
} from "./evidence-photos.types";

/**
 * connection.js resolves failures as `{ success: false, error }` instead of
 * rejecting. Reject here, keeping the server's `message`, so react-query and
 * the Uploader see a failure. The axios interceptor already toasts HTTP
 * errors, so callers must not add a second snackbar for those.
 *
 * `requireData` is false for DELETE, which returns no data.
 */
const assertOk = <T>(
  res: any,
  fallbackMessage: string,
  requireData = true
): T => {
  if (res?.success === false || (requireData && res?.data == null)) {
    const message = res?.error?.response?.data?.message ?? res?.message;
    throw new Error(
      typeof message === "string" && message ? message : fallbackMessage
    );
  }
  return res as T;
};

const base = (evidence_id: number) => `/evidence/${evidence_id}/photos`;

const get = async (
  evidence_id: number,
  params: GetEvidencePhotosQuery = {}
): Promise<GetEvidencePhotosResult> => {
  const res = await httpGet(`${base(evidence_id)}/files`, params);
  return assertOk<GetEvidencePhotosResult>(res, "Failed to load photos");
};

const startUpload = async (
  evidence_id: number,
  data: StartEvidencePhotoUploadBody
): Promise<StartEvidencePhotoUploadResult> => {
  const res = await httpPost(`${base(evidence_id)}/start-upload`, data);
  const ok = assertOk<StartEvidencePhotoUploadResult>(
    res,
    "Failed to start photo upload"
  );
  if (!ok.data.uploadId || !Array.isArray(ok.data.signedUrls)) {
    throw new Error("Failed to start photo upload");
  }
  return ok;
};

const completeUpload = async (
  evidence_id: number,
  photo_uuid: string,
  body: CompleteEvidencePhotoUploadBody
): Promise<CompleteEvidencePhotoUploadResult> => {
  const res = await httpPost(
    `${base(evidence_id)}/${photo_uuid}/complete-upload`,
    body
  );
  return assertOk<CompleteEvidencePhotoUploadResult>(
    res,
    "Failed to complete photo upload"
  );
};

// The evidence photo route writes the evidence_photo:delete activity log.
const deletePhoto = async ({
  evidence_id,
  photo_uuid,
}: DeleteEvidencePhotoData): Promise<void> => {
  const res = await httpDelete(`${base(evidence_id)}/${photo_uuid}`);
  assertOk(res, "Failed to delete photo", false);
};

// No evidence photo rename route: renames go through the Files API, which
// allows them inside the managed tree whatever the flag.
const rename = async ({
  photo_uuid,
  name,
}: RenameEvidencePhotoData): Promise<void> => {
  const res = await FilesAPI.update({ uuid: photo_uuid, name });
  assertOk(res, "Failed to rename photo", false);
};

/**
 * Routes for the shared Uploader. No `markFailed`: the evidence photo routes
 * have no PATCH, and the Uploader skips it when `startUpload` is custom.
 */
const uploaderApi = (evidence_id: number): UploaderApi => ({
  startUpload: ({ uuid, parts, file }) =>
    startUpload(evidence_id, {
      uuid,
      name: file.name,
      size: file.size as number,
      // The Uploader's parts carry ETag: ""; the API schema is strict per part.
      parts: parts.map(({ PartNumber }) => ({ PartNumber })),
      client_modified: file.client_modified,
    }),
  completeUpload: ({ uuid, uploadId, parts, md5, sha1, sha256 }) =>
    completeUpload(evidence_id, uuid, {
      uploadId,
      parts,
      md5,
      sha1,
      sha256,
    }),
});

export const EvidencePhotosAPI = {
  get,
  startUpload,
  completeUpload,
  delete: deletePhoto,
  rename,
  uploaderApi,
};
